/**
 * Geldformattering zonder floats.
 *
 * De API stuurt bedragen als string met twee decimalen (`"1450.00"`) juist om
 * afrondingsfouten in JavaScript te vermijden - zie `02-api-contract.md`
 * §Algemeen. De UI maakt daar dus geen `Number` van: hier wordt puur op de tekst
 * gewerkt. Wat de API stuurt is wat de gebruiker ziet.
 */

/** `"1450.00"` -> `{ negative: false, whole: "1450", fraction: "00" }` */
function split(amount: string): { negative: boolean; whole: string; fraction: string } {
    const trimmed = amount.trim();
    const negative = trimmed.startsWith('-');
    const unsigned = trimmed.replace(/^[+-]/, '');
    const [whole = '0', fraction = ''] = unsigned.split('.');

    return {
        negative,
        whole: whole.replace(/\D/g, '').replace(/^0+(?=\d)/, '') || '0',
        fraction: `${fraction.replace(/\D/g, '')}00`.slice(0, 2),
    };
}

/** Duizendtallen met een punt: `1450` -> `1.450`. */
function groupThousands(whole: string): string {
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** True als het bedrag nul is: `"0.00"`, `"-0.00"`, `"0"`. */
export function isZeroAmount(amount: string): boolean {
    const { whole, fraction } = split(amount);

    return whole === '0' && fraction === '00';
}

/**
 * `"1450.00"` -> `€ 1.450`, `"1250.50"` -> `€ 1.250,50`.
 *
 * Zonder centen tenzij ze ertoe doen (`docs/woordenlijst.md`). Een negatief
 * bedrag krijgt een echt minteken (U+2212): `− € 25`.
 */
export function formatMoney(amount: string | null | undefined): string {
    if (amount === null || amount === undefined || amount.trim() === '') {
        return '€ —';
    }

    const { negative, whole, fraction } = split(amount);
    const cents = fraction === '00' ? '' : `,${fraction}`;
    const value = `€ ${groupThousands(whole)}${cents}`;

    return negative && !isZeroAmount(amount) ? `− ${value}` : value;
}

/** Een btw-tarief als percentage: `"21.00"` -> `21%`, `"9.50"` -> `9,5%`. */
export function formatPercentage(rate: string): string {
    const { whole, fraction } = split(rate);
    const decimals = fraction.replace(/0+$/, '');

    return decimals === '' ? `${whole}%` : `${whole},${decimals}%`;
}

/**
 * `"495,50"` of `"€ 1.450,00"` -> `"495.50"` / `"1450.00"`: de schrijfwijze die
 * de API verwacht.
 *
 * Puur tekstwerk, net als de rest van dit bestand: er komt geen `Number` aan te
 * pas, want dat is precies waar de cent verdwijnt. Een lege invoer blijft leeg;
 * de API zegt dan zelf dat er een bedrag ontbreekt.
 */
export function toDecimalAmount(value: string): string {
    const compact = value.replace(/[\u20ac\s]/g, '');

    return compact.includes(',') ? compact.replace(/\./g, '').replace(',', '.') : compact;
}
